import * as React from "react";
import { TextField as MUITextField, TextFieldProps, Typography, useTheme } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { tokens } from "../../theme";

export type InputFieldProps = {
    color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
    size?: "small" | "medium";
    label?: any | "";
    labelAbove?: boolean;
    variant?: "filled" | "outlined" | "standard" | undefined;
    name?: string;
    control?: Control<any, any>;
    rules?: Object;
    isDisabled?: boolean;
    maxLength?: number;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    controllerName?: string;
    isEmpty?: boolean;
    isNumberOnly?: boolean;
    readOnly?: boolean;
    allowSpecialCharacters?: boolean;
} & TextFieldProps;

const CustomTextField: React.FC<InputFieldProps> = React.forwardRef(({
    size,
    color,
    label,
    labelAbove = false,
    variant,
    name,
    control,
    rules,
    isDisabled,
    maxLength,
    onChange,
    onBlur,
    controllerName,
    isEmpty,
    isNumberOnly,
    readOnly,
    allowSpecialCharacters = true,
    sx,
    inputProps: inputPropsProp,
    helperText: helperTextProp,
    placeholder,
    ...props
}, ref) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isFocused, setIsFocused] = React.useState(false);

    const isRequired = () =>
        !!(rules && typeof rules === "object" && "required" in rules && label);

    const getLabel = () => {
        if (isRequired()) {
            return (
                <span>
                    {label}{" "}
                    <span style={{ color: colors.success[200], fontSize: "18px" }}>*</span>
                </span>
            );
        }
        return label;
    };

    const SIZE_STYLE_MAP = {
        small:  { height: 40, padding: "8px 12px" },
        medium: { height: 48, padding: "10px 14px" },
    };

    const isMultiline = !!props.multiline;

    const internalSx = {
        "& .MuiOutlinedInput-root": {
            ...(size && !isMultiline && {
                height: SIZE_STYLE_MAP[size ?? "medium"]?.height,
                "& input": {
                    padding: SIZE_STYLE_MAP[size ?? "medium"]?.padding,
                },
            }),
            ...(size && isMultiline && {
                "& textarea": {
                    padding: SIZE_STYLE_MAP[size ?? "medium"]?.padding,
                    boxSizing: "border-box",
                    minHeight: size === "small" ? 72 : 96,
                    maxHeight: 20,
                    resize: "vertical",
                },
            }),
            "& fieldset": {
                borderColor:
                    theme.palette.mode === "dark" ? "" : "#14E2E2",
            },
            "&.Mui-disabled": {
                "& fieldset": {
                    borderColor:
                        isDisabled && theme.palette.mode === "light"
                            ? "#14E2E2"
                            : "",
                },
                "& input, & textarea": {
                    color:
                        isDisabled && theme.palette.mode === "light"
                            ? colors.grey[300]
                            : "",
                },
            },
        },
        "& .MuiInputBase-input.Mui-disabled, & textarea.Mui-disabled": {
            WebkitTextFillColor:
                isDisabled && theme.palette.mode === "light"
                    ? colors.grey[300]
                    : "",
        },
        "& .MuiInputLabel-outlined": {
            color: theme.palette.mode === "dark" ? "" : colors.primary[100],
            "&.Mui-disabled": {
                color:
                    isDisabled && theme.palette.mode === "light"
                        ? colors.grey[300]
                        : "",
            },
        },
    };

    return (
        <Controller
            name={controllerName || ""}
            control={control}
            rules={rules ?? {}}
            render={({ field, fieldState }) => (
                <>
                    {labelAbove && (
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: theme.palette.text.secondary, mb: 0.7, lineHeight: "16px" }}>
                            {label}
                        </Typography>
                    )}
                <MUITextField
                    id={field.name}
                    {...field}
                    {...props}
                    inputRef={ref}
                    value={isEmpty ? "" : field.value}
                    name={name ?? controllerName}
                    label={labelAbove ? "" : (getLabel() || "")}
                    placeholder={labelAbove ? (placeholder || (typeof label === "string" ? label : "")) : placeholder}
                    fullWidth
                    type="text"
                    onChange={(e: any) => {
                        let value = e.target.value;
                        if (isNumberOnly) {
                            value = value.replace(/[^0-9.]/g, "");
                            if (value.split(".").length > 2) {
                                value = value.slice(0, -1);
                            }
                        }
                        if (allowSpecialCharacters === false) {
                            value = value.replace(/[^a-zA-Z0-9 ]/g, "");
                        }
                        field.onChange(value);
                        onChange && onChange(e);
                    }}
                    onFocus={(e) => { setIsFocused(true); props.onFocus?.(e as any); }}
                    onBlur={(e: any) => {
                        setIsFocused(false);
                        field.onChange(e.target.value?.trim());
                        field.onBlur();
                        onBlur && onBlur(e);
                    }}
                    size={size ?? "medium"}
                    color={
                        color ??
                        (theme.palette.mode === "dark" ? "secondary" : "primary")
                    }
                    variant={variant ?? "outlined"}
                    InputLabelProps={{
                        shrink: isDisabled ? true : (isFocused || Boolean(field.value)),
                    }}
                    inputProps={{
                        ...(inputPropsProp ?? {}),
                        ...(maxLength ? { maxLength } : {}),
                        readOnly: readOnly ?? false,
                    }}
                    error={!!fieldState.error}
                    helperText={
                        fieldState.error
                            ? fieldState.error.message
                            : helperTextProp ?? null
                    }
                    disabled={isDisabled ?? props.disabled}
                    sx={[internalSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
                />
                </>
            )}
        />
    );
});

export default CustomTextField;